import {
  CROSS_PIECE_HEIGHT,
  CrossPieceType,
  GLASS_COLOR,
  LOCK_TRIANGLE_REFERENCE,
  Material,
  MIN_WIDTH_NEED_REINFORCE,
  PaneMaterial,
  PlateType,
  Position,
  PositionAngle,
  THICKNESS_PANE_DEFAULT,
  THICKNESS_PLATE_DEFAULT,
} from '../constants';
import { CustomPiece, KewloxPieceType } from '../pieces/CustomPiece';
import { RackJson } from '../Data';
import { Pane } from '../pieces/Pane';
import { CrossPiece } from '../pieces/CrossPiece';

import { Color } from './Color';
import { getPiecesData } from '../utils/dataStorage';
import { PiecesList } from './PiecesList';
import { Batten } from '../pieces/Batten';
import { Plate } from '../pieces/Plate';
import cloneDeep from 'lodash/cloneDeep';
import { PositionPiece } from '../type';
import { findCrossPiece, findPane, findPlate } from '../utils/piecesUpdater';
import { findColor } from '../utils/colorHelper';
import { SimpleComponent } from '../pieces/SimpleComponent';
import { findPieceByRef } from '../utils/helpers';

export class Rack {
  public level: number;
  private _height: number;
  public crossPieces: CustomPiece<CrossPiece>[];
  public panes: CustomPiece<Pane>[];
  public battens: CustomPiece<Batten>[];
  public plate?: CustomPiece<Plate>;
  public glassPlate?: CustomPiece<Plate>;
  public others?: CustomPiece<SimpleComponent>[];
  private _frontCrossPieceReinforcedByUser: boolean;

  constructor(
    level: number,
    height: number,
    crossPieces: CustomPiece<CrossPiece>[],
    panes: CustomPiece<Pane>[],
    battens: CustomPiece<Batten>[],
    plate?: CustomPiece<Plate>,
    glassPlate?: CustomPiece<Plate>,
    others?: CustomPiece<SimpleComponent>[],
  ) {
    this.level = level;
    this._height = height;
    this.crossPieces = crossPieces;
    this.panes = panes;
    this.battens = battens;
    if (plate) this.plate = plate;
    if (glassPlate) this.glassPlate = glassPlate;
    if (others) this.others = others;
    this._frontCrossPieceReinforcedByUser = false;
    this.updateLockingTriangle();
  }

  static fromModel(model: RackJson, pieces?: PiecesList) {
    const { level, height } = model;

    if (!pieces) pieces = getPiecesData();

    const crossPieces: CustomPiece<CrossPiece>[] = [];
    if (model.crossPieces) {
      for (const item of model.crossPieces) {
        crossPieces.push(
          CustomPiece.fromModel<CrossPiece>(item, pieces.crossPieces),
        );
      }
      crossPieces.sort(CustomPiece.compareTo);
    }
    const panes: CustomPiece<Pane>[] = [];
    if (model.panes) {
      for (const item of model.panes) {
        panes.push(CustomPiece.fromModel<Pane>(item, pieces.panes));
      }
      panes.sort(CustomPiece.compareTo);
    }
    const battens: CustomPiece<Batten>[] = [];
    if (model.battens) {
      for (const item of model.battens) {
        battens.push(CustomPiece.fromModel<Batten>(item, pieces.battens));
      }
      battens.sort(CustomPiece.compareTo);
    }
    let plate: CustomPiece<Plate> | undefined = undefined;
    let glassPlate: CustomPiece<Plate> | undefined = undefined;
    if (model.plate) {
      const plateType = Rack.getDefaultPlateType(
        Rack.acceptedPlatesTypes(
          crossPieces.find(e => e.position === Position.Front)?.base.type,
          crossPieces.find(e => e.position === Position.Back)?.base.type,
        ),
      );

      plate = CustomPiece.fromModel<Plate>(
        model.plate,
        pieces.plates,
        plateType,
      );
      if (model.glassPlate) {
        glassPlate = CustomPiece.fromModel<Plate>(
          model.glassPlate,
          pieces.plates,
          plateType,
        );
      }
    }
    return new Rack(
      level,
      height,
      crossPieces,
      panes,
      battens,
      plate,
      glassPlate,
    );
  }

  toJSON() {
    return Object.assign({}, this, { height: this._height });
  }

  static replacer(key: string, value: any): any {
    // Filtering out properties for serialization
    if (value[0] === '_') {
      return undefined;
    }
    return value;
  }

  updateWidth(width: number, isTop = false, shelfHasBackDoor = false) {
    // Only the first rack contains the front and back crossPiece
    //  if (this.level !== 0) return;

    let cpType = undefined;
    const cp = this.getCrossPiece(Position.Front);
    // Change type here to avoid error because no one renforced EP exist in stock
    if (
      width >= MIN_WIDTH_NEED_REINFORCE &&
      !isTop &&
      cp?.base.type === CrossPieceType.UpperEmptyLowerPane
    )
      cpType = CrossPieceType.UpperEmptyLowerDoors;

    let reinforce = undefined;
    if (width >= MIN_WIDTH_NEED_REINFORCE) reinforce = true;
    else if (!this.frontCrossPieceReinforcedByUser) reinforce = false;

    this.updateCrossPiece(Position.Front, width, undefined, cpType, reinforce);

    // Reinforce back cross piece if needed by width AND by back doors
    // Type defined here because reinforced cross pieces exist only in ED/DD types
    if (
      this.level === 0 &&
      shelfHasBackDoor &&
      width >= MIN_WIDTH_NEED_REINFORCE
    )
      this.updateCrossPiece(
        Position.Back,
        width,
        undefined,
        CrossPieceType.UpperDoorsLowerDoors,
        true,
      );
    else
      this.updateCrossPiece(Position.Back, width, undefined, undefined, false);
    if (this.plate) this.updatePlate(this.plate.height, width);

    this.updatePane(Position.Back, null, width);
  }

  set height(height: number) {
    /**
     * Update the height of the rack
     */
    if (height === 0) {
      this.panes = [];
      this.battens = [];
    } else {
      for (const position of Object.values(PositionAngle))
        this.addOrUpdateBatten(height, position);

      for (const position of Object.values(Position))
        this.updatePane(position, height);
    }

    this._height = height;
  }

  get height() {
    return this._height;
  }

  get frontCrossPieceReinforcedByUser() {
    return this._frontCrossPieceReinforcedByUser;
  }

  set frontCrossPieceReinforcedByUser(
    frontCrossPieceReinforcedByUser: boolean,
  ) {
    this._frontCrossPieceReinforcedByUser = frontCrossPieceReinforcedByUser;
  }

  getPane(position: Position) {
    return this.panes.find(item => item.position === position);
  }

  getCrossPiece(position: Position) {
    return this.crossPieces.find(item => item.position === position);
  }

  getBatten(position: PositionAngle) {
    return this.battens.find(item => item.position === position);
  }

  clone(): Rack {
    return cloneDeep(this);
  }

  updateRackElements<T extends KewloxPieceType>(
    rackElements: CustomPiece<T>[],
    element: CustomPiece<T>,
    position: PositionPiece,
  ): CustomPiece<T>[] {
    // Remove previous entry for this position
    rackElements = rackElements.filter(item => item.position !== position);
    rackElements.push(element);
    return rackElements;
  }

  updatePane(
    position: Position,
    height?: number | null,
    width?: number | null,
    material?: PaneMaterial | null,
    color?: Color | null,
  ) {
    const pane = this.getPane(position);
    if (pane) {
      const customP = findPane(position, pane, height, width, material, color);
      this.panes = this.updateRackElements<Pane>(this.panes, customP, position);
    }
  }

  updateCrossPiece(
    position: Position,
    width?: number,
    material?: Material,
    type?: CrossPieceType,
    reinforce?: boolean,
    rod?: boolean,
    drill?: boolean,
  ) {
    const crossPiece = this.getCrossPiece(position);
    if (crossPiece) {
      const customCP = findCrossPiece(
        position,
        crossPiece,
        width,
        material,
        type,
        reinforce,
        rod,
        drill,
      );
      this.crossPieces = this.updateRackElements<CrossPiece>(
        this.crossPieces,
        customCP,
        position,
      );
      return this.getCrossPiece(position);
    }
  }

  addOrUpdateBatten(height: number, position: PositionAngle) {
    const batten = this.getBatten(position);
    if (batten) {
      const searchInfo = Batten.getSearchInfos(height, batten.base.material);
      const customCP = getPiecesData().battens.getCustom(searchInfo, position);
      this.battens = this.updateRackElements<Batten>(
        this.battens,
        customCP,
        position,
      );
    } else {
      const searchInfo = Batten.getSearchInfos(height, Material.Wood);
      const customCP = getPiecesData().battens.getCustom(searchInfo, position);
      this.battens = this.updateRackElements<Batten>(
        this.battens,
        customCP,
        position,
      );
    }
  }

  updatePlate(
    depth?: number | null,
    width?: number | null,
    material?: PaneMaterial | null,
    color?: Color | null,
    glass?: boolean,
    type?: PlateType,
  ) {
    const plate = this.plate;
    if (plate) {
      if (glass || (typeof glass === 'undefined' && this.glassPlate)) {
        this.glassPlate = findPlate(
          plate,
          depth,
          width,
          PaneMaterial.Glass,
          findColor(GLASS_COLOR),
          type,
        );
        this.plate = findPlate(
          plate,
          depth,
          width,
          material,
          color,
          type,
          THICKNESS_PANE_DEFAULT,
        );
        return;
      } else if (typeof glass === 'boolean' && !glass) {
        this.glassPlate = undefined;
      }
      this.plate = findPlate(
        plate,
        depth,
        width,
        material,
        color,
        type,
        THICKNESS_PLATE_DEFAULT,
      );
    }
  }

  get price(): number {
    let price = 0;
    for (const crossPiece of this.crossPieces) price += crossPiece.price;
    for (const pane of this.panes) price += pane.price;
    for (const batten of this.battens) price += batten.price;
    if (this.plate) price += this.plate.price;
    if (this.glassPlate) price += this.glassPlate.price;
    if (this.others) {
      for (const element of this.others) {
        price += element.price * element.quantity;
      }
    }

    return Math.round(price * 100) / 100;
  }

  get weight(): number {
    let weight = 0;
    for (const crossPiece of this.crossPieces) weight += crossPiece.weight;
    for (const pane of this.panes) weight += pane.weight;
    for (const batten of this.battens) weight += batten.weight;
    if (this.plate) weight += this.plate.weight;
    if (this.glassPlate) weight += this.glassPlate.weight;
    if (this.others) {
      for (const element of this.others) {
        weight += element.weight * element.quantity;
      }
    }

    return weight;
  }

  get realHeight() {
    /**
     * Add cross piece height to the rack height for the real height
     */
    return this._height + CROSS_PIECE_HEIGHT;
  }

  resetRack(width: number, withBackPane = false) {
    /**
     * Set the rack as empty
     */
    if (withBackPane) {
      // Add standard back pane if necessary
      if (!this.getPane(Position.Back))
        this.panes.push(Pane.standardPane(this.height, width, Position.Back));
    }

    // Cross Pieces Manager will add / update cross pieces if needed
  }

  removePlate() {
    /**
     * Remove plate
     */
    this.plate = undefined;
    this.glassPlate = undefined;
  }

  removePane(position: Position) {
    /**
     * Remove panes of the position in params
     */
    const pane = this.getPane(position);
    this.panes = this.panes.filter(e => e.position !== position);
    if (pane) {
      // Important: We must here update manually the cross piece because
      // cross piece types PP remain acceptable after removing pane but
      // we prefer no groove by default when removing pane ; the user can add groove after if needed
      this.updateCrossPiece(
        position,
        undefined,
        undefined,
        CrossPieceType.UpperEmptyLowerPane,
      );
    }
  }

  addCrossPiece(
    position: Position,
    width: number,
    material = Material.Wood,
    type: CrossPieceType,
    reinforce = false,
    rod = false,
    drill = false,
  ) {
    this.crossPieces = this.updateRackElements<CrossPiece>(
      this.crossPieces,
      CrossPiece.standardCrossPiece(width, position),
      position,
    );
    this.updateCrossPiece(
      position,
      width,
      material,
      type,
      reinforce,
      rod,
      drill,
    );
    this.updateLockingTriangle();
  }

  removeCrossPiece(position: Position) {
    this.crossPieces = this.crossPieces.filter(e => e.position !== position);
    this.updateLockingTriangle();
  }

  updateDepth(newDepth: number) {
    this.updatePane(Position.Left, null, newDepth);
    this.updatePane(Position.Right, null, newDepth);
    this.updateCrossPiece(Position.Left, newDepth);
    this.updateCrossPiece(Position.Right, newDepth);
    this.updatePlate(newDepth, null);
  }

  public getC4Number(): number {
    return (4 - this.crossPieces.length) * 2;
  }

  public updateLockingTriangle() {
    const numberC4 = this.getC4Number();
    if (numberC4 > 0) {
      if (!this.others) this.others = [];
      // Remove old C4
      this.others = this.others?.filter(e => e.reference !== 'C4');

      const lockTriangle = findPieceByRef(LOCK_TRIANGLE_REFERENCE);

      if (!lockTriangle) {
        throw new Error('There is no locking triangle in the pieces list');
      }

      const lockTriangleCustomPiece = new CustomPiece(
        lockTriangle,
        false,
        Position.Front,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        numberC4,
      );

      this.others?.push(lockTriangleCustomPiece);
    }
  }

  hasPaneForPosition(position: Position) {
    return !!this.getPane(position);
  }

  setDrillCrossPiece(value: boolean, position: Position = Position.Back) {
    this.updateCrossPiece(
      position,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      value,
    );
  }

  // Rules tools
  setFrontCrossPiece(crossPieceType: CrossPieceType) {
    this.updateCrossPiece(Position.Front, undefined, undefined, crossPieceType);
  }

  static acceptedPlatesTypes(
    frontCPtype: CrossPieceType | undefined,
    backCPtype: CrossPieceType | undefined,
  ): PlateType[] {
    if (!frontCPtype || !backCPtype)
      return [PlateType.DoorsDoors, PlateType.PanePane, PlateType.DoorsPane];

    if (
      (frontCPtype === CrossPieceType.UpperDoorsLowerDoors ||
        frontCPtype === CrossPieceType.UpperEmptyLowerDoors) &&
      (backCPtype === CrossPieceType.UpperDoorsLowerDoors ||
        backCPtype === CrossPieceType.UpperEmptyLowerDoors)
    )
      return [PlateType.DoorsDoors];

    if (
      (frontCPtype === CrossPieceType.UpperPaneLowerPane ||
        frontCPtype === CrossPieceType.UpperEmptyLowerPane) &&
      (backCPtype === CrossPieceType.UpperPaneLowerPane ||
        backCPtype === CrossPieceType.UpperEmptyLowerPane)
    )
      return [PlateType.PanePane];

    return [PlateType.DoorsPane];
  }

  static getDefaultPlateType(types: PlateType[]): PlateType {
    if (types.length === 1) return types[0];
    return PlateType.DoorsPane;
  }
}
